import { createRouter } from '@tanstack/react-router'
import { QueryClient,QueryCache } from '@tanstack/react-query'
import { isAxiosError } from '@/lib/api-client'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { routeTree } from './routeTree.gen'
import { initSentry } from '@/sentry'
import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'

// Every deploy renames the hashed chunks; a tab opened before the deploy will
// fail its next lazy route import (the server's SPA fallback answers with
// index.html → "disallowed MIME type" errors). Vite fires `vite:preloadError`
// for exactly this — reload once to pick up the fresh index.html. The
// sessionStorage stamp stops a reload loop if the failure is something else.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    const KEY = 'rp_chunk_reload_at'
    const last = Number(window.sessionStorage.getItem(KEY) ?? 0)
    if (Date.now() - last < 30_000) return
    event.preventDefault()
    window.sessionStorage.setItem(KEY, String(Date.now()))
    window.location.reload()
  })
}

export const queryClient = new QueryClient({
  // Capture query (read) failures in Sentry — previously only mutations were
  // reported, so failing GETs were invisible in error monitoring.
  queryCache: new QueryCache({
    onError: (error) => {
      // 401/403 are expected auth redirects, not bugs — don't report them.
      if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
        return
      }
      Sentry.captureException(error)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
          return false
        }
        return failureCount < 1
      },
    },
    mutations: {
      onError: (error: Error) => {
        const message = isAxiosError(error)
          ? error.response?.data?.message ?? error.message ?? 'Action failed'
          : error instanceof Error ? error.message : 'Action failed'

        toast.error(message)

        // Expected client/business outcomes are surfaced to the user via the
        // toast above but are NOT bugs, so they must not pollute Sentry:
        //  400      — business-rule rejections (dual control, cash caps, DOB
        //             rules) shown to the cashier; server-side 400 DEFECTS
        //             (Prisma query construction) are captured by the BACKEND
        //             error handler, so skipping here loses nothing.
        //  401/403 — auth/permission (handled by the auth layer)
        //  409      — optimistic-lock / order-lock contention ("in use by …")
        //  422      — request rejected by server-side validation
        // Genuine 5xx and non-HTTP errors still report.
        const status = isAxiosError(error) ? error.response?.status : undefined
        const expected = status === 400 || status === 401 || status === 403 || status === 409 || status === 422
        if (expected) return

        Sentry.captureException(error)
      },
    },
  },
})

export function getRouter() {
  const router = createRouter({
    routeTree,
    context: {
      queryClient,
    },
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultErrorComponent: RouteError,
    defaultPendingComponent: RoutePending,
  })
  // Initialize Sentry with the router so transactions are named by parameterized
  // route. Safe to call once; subsequent calls are no-ops.
  initSentry(router)
  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
